import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';
import { preloadImage } from '@theme/utilities';

const INLINE_ZOOM_SCALE = 2;

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    if (!this.classList.contains('media-gallery--inline-zoom')) return;

    this.refs.slideshow?.addEventListener('slideshow:select', this.#resetAllInlineZoom, { signal });
    this.addEventListener('pointerdown', this.#handleInlineZoomPointerDown, { signal });
    this.addEventListener('pointermove', this.#handleInlineZoomPointerMove, { signal });
    this.addEventListener('pointerup', this.#handleInlineZoomPointerUp, { signal });
    this.addEventListener('pointercancel', this.#handleInlineZoomPointerUp, { signal });
    this.addEventListener('touchstart', this.#stopTouchPropagationWhenInlineZoomed, { signal });
    this.addEventListener('touchmove', this.#stopTouchPropagationWhenInlineZoomed, { signal, passive: false });
  }

  #controller = new AbortController();
  /** @type {HTMLElement | null} */
  #inlineZoomPanContainer = null;
  /** @type {{ x: number; y: number; scrollLeft: number; scrollTop: number } | null} */
  #inlineZoomPanStart = null;
  /** @type {boolean} */
  #inlineZoomDidPan = false;

  static #INLINE_ZOOM_PAN_THRESHOLD = 4;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Toggles in-container zoom for the media container that triggered the event.
   * @param {Event} event
   */
  toggleInlineZoom(event) {
    event?.preventDefault();
    event?.stopPropagation();

    // Click still fires after a mouse drag; ignore it so pan doesn't zoom out.
    if (this.#inlineZoomDidPan) {
      this.#inlineZoomDidPan = false;
      return;
    }

    const container =
      event.target instanceof Element ? event.target.closest('.product-media-container') : null;
    if (!container) return;

    if (container.classList.contains('is-inline-zoomed')) {
      this.#resetInlineZoom(container);
      return;
    }

    this.querySelectorAll('.product-media-container.is-inline-zoomed').forEach((activeContainer) => {
      if (activeContainer !== container) this.#resetInlineZoom(activeContainer);
    });

    this.#applyInlineZoomDimensions(container);
    container.classList.add('is-inline-zoomed');
    container.style.setProperty('--inline-zoom-scale', String(INLINE_ZOOM_SCALE));
    container.closest('slideshow-slide')?.setAttribute('data-inline-zoom-active', '');
    this.#loadInlineZoomHighResImage(container);
    this.#updateInlineZoomButton(container, true);
    this.#centerInlineZoom(container);
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }

  #resetAllInlineZoom = () => {
    this.querySelectorAll('.product-media-container.is-inline-zoomed').forEach((container) => {
      this.#resetInlineZoom(container);
    });
  };

  /**
   * @param {HTMLElement} container
   * @returns {HTMLElement}
   */
  #getInlineZoomViewport(container) {
    const viewport = container.querySelector('.product-media-inline-zoom-viewport');
    return viewport instanceof HTMLElement ? viewport : container;
  }

  /**
   * @param {HTMLElement} container
   */
  #resetInlineZoom(container) {
    container.classList.remove('is-inline-zoomed');
    container.style.removeProperty('--inline-zoom-img-width');
    container.style.removeProperty('--inline-zoom-img-height');
    const viewport = this.#getInlineZoomViewport(container);
    viewport.scrollTop = 0;
    viewport.scrollLeft = 0;
    container.closest('slideshow-slide')?.removeAttribute('data-inline-zoom-active');
    this.#updateInlineZoomButton(container, false);

    if (this.#inlineZoomPanContainer === viewport) {
      this.#inlineZoomPanContainer = null;
      this.#inlineZoomPanStart = null;
    }
  }

  /**
   * Captures the rendered image size so zoom preserves its aspect ratio.
   * @param {HTMLElement} container
   */
  #applyInlineZoomDimensions(container) {
    const image = container.querySelector('.product-media__image');
    if (!(image instanceof HTMLImageElement)) return;

    const { width, height } = image.getBoundingClientRect();
    if (!width || !height) return;

    container.style.setProperty('--inline-zoom-img-width', `${width}px`);
    container.style.setProperty('--inline-zoom-img-height', `${height}px`);
  }

  /**
   * @param {HTMLElement} container
   */
  #centerInlineZoom(container) {
    const viewport = this.#getInlineZoomViewport(container);

    requestAnimationFrame(() => {
      const scrollLeft = (viewport.scrollWidth - viewport.clientWidth) / 2;
      const scrollTop = (viewport.scrollHeight - viewport.clientHeight) / 2;

      viewport.scrollLeft = Math.max(0, scrollLeft);
      viewport.scrollTop = Math.max(0, scrollTop);
    });
  }

  /**
   * @param {HTMLElement} container
   */
  #loadInlineZoomHighResImage(container) {
    const image = container.querySelector('.product-media__image');
    if (!(image instanceof HTMLImageElement)) return;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || image.dataset.inlineZoomHiResLoaded === 'true') return;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);
    newImage.dataset.inlineZoomHiResLoaded = 'true';

    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#centerInlineZoom(container);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * @param {HTMLElement} container
   * @param {boolean} zoomed
   */
  #updateInlineZoomButton(container, zoomed) {
    const button = container.querySelector('.product-media-container__zoom-button');
    if (!(button instanceof HTMLButtonElement)) return;

    button.setAttribute('aria-pressed', `${zoomed}`);
    button.classList.toggle('is-zoomed', zoomed);
  }

  /**
   * @param {PointerEvent} event
   */
  #handleInlineZoomPointerDown = (event) => {
    if (event.pointerType === 'touch') return;

    const container = event.target instanceof Element ? event.target.closest('.product-media-container.is-inline-zoomed') : null;
    if (!container || event.target instanceof Element && event.target.closest('.product-media-container__zoom-button')) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const viewport = this.#getInlineZoomViewport(container);
    this.#inlineZoomDidPan = false;
    this.#inlineZoomPanContainer = viewport;
    this.#inlineZoomPanStart = {
      x: event.clientX,
      y: event.clientY,
      scrollLeft: viewport.scrollLeft,
      scrollTop: viewport.scrollTop,
    };

    viewport.setPointerCapture(event.pointerId);
  };

  /**
   * @param {PointerEvent} event
   */
  #handleInlineZoomPointerMove = (event) => {
    if (!this.#inlineZoomPanContainer || !this.#inlineZoomPanStart) return;

    event.preventDefault();

    const dx = event.clientX - this.#inlineZoomPanStart.x;
    const dy = event.clientY - this.#inlineZoomPanStart.y;

    if (
      !this.#inlineZoomDidPan &&
      (Math.abs(dx) > MediaGallery.#INLINE_ZOOM_PAN_THRESHOLD ||
        Math.abs(dy) > MediaGallery.#INLINE_ZOOM_PAN_THRESHOLD)
    ) {
      this.#inlineZoomDidPan = true;
    }

    this.#inlineZoomPanContainer.scrollLeft = this.#inlineZoomPanStart.scrollLeft - dx;
    this.#inlineZoomPanContainer.scrollTop = this.#inlineZoomPanStart.scrollTop - dy;
  };

  /**
   * @param {PointerEvent} event
   */
  #handleInlineZoomPointerUp = (event) => {
    if (!this.#inlineZoomPanContainer) return;

    if (this.#inlineZoomPanContainer.hasPointerCapture(event.pointerId)) {
      this.#inlineZoomPanContainer.releasePointerCapture(event.pointerId);
    }

    this.#inlineZoomPanContainer = null;
    this.#inlineZoomPanStart = null;

    if (!this.#inlineZoomDidPan) return;

    // A click still synthesizes after pointerup; stop it before toggleInlineZoom runs.
    const suppressClick = (clickEvent) => {
      clickEvent.preventDefault();
      clickEvent.stopImmediatePropagation();
      this.#inlineZoomDidPan = false;
    };

    this.addEventListener('click', suppressClick, { capture: true, once: true });

    // If no click follows (pointer left the target), don't leave the flag stuck.
    setTimeout(() => {
      this.removeEventListener('click', suppressClick, true);
      this.#inlineZoomDidPan = false;
    }, 50);
  };

  #stopTouchPropagationWhenInlineZoomed = (event) => {
    const container = event.target instanceof Element ? event.target.closest('.product-media-container.is-inline-zoomed') : null;
    if (!container) return;
    event.stopPropagation();
  };
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
